import enUS from "date-fns/locale/en-US";
import {
    IACLEmail,
    IAuthUser,
    IntakeConfig,
    TOffices,
    TOfficeManager,
    TRequestedService,
    ConsentFormCache,
    EmailSignatureCache,
    TLocationAddress,
} from "intake_shared/src/types";
import React, { useEffect, useState } from "react";
import { CookiesProvider } from "react-cookie";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import AccessControlledRoute from "./common/AccessControlRoute";
import "./v2/styles/Appv2.scss";
import "./App.scss";
import { AclContext } from "./contexts/AclContext";
import { CacheContext } from "./contexts/CacheContext";
import { ChartLockProvider } from "./contexts/ChartLockContext";
import { accessControlledRoutes, getRoutePaths } from "./navigation/routes";
import store from "./store";
import {
    getAllOffices,
    getLocationAddress,
    getOfficeManagers,
} from "./store/cache/location";
import {
    DepartmentCache,
    getDepartments,
    getRequestedServices,
} from "./store/cache/requested_service";
import { dbv9, firebaseAuth, fxs } from "./utils/firestore";
import { doc, onSnapshot, Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { updateProfile } from "firebase/auth";

import { getConsentForms } from "./store/consent_form";
import { getEmailSignatures } from "./store/email-signatures";
import Loader from "./v2/components/bits/Loader/Loader";

registerLocale("enUS", enUS);
setDefaultLocale("enUS");

function router(
    authUser: IAuthUser,
    userAcl: IACLEmail,
    intakeConfig: IntakeConfig,
    {
        offices,
        allUserRoles,
        updateOffices,
        updateOfficeManager,
        office_managers,
        departments,
        consentForms,
        emailSignatures,
        location_address,
        allDepartmentAndServices,
        requestedServices,
        updateRequestedServices,
        updateDepartments,
        updateConsentForms,
        updateEmailSignatures,
        updateAllDeptAndServices,
        updateLocationAddress,
    }: {
        offices?: TOffices;
        allUserRoles?: IACLEmail[];
        updateOfficeManager: React.Dispatch<
            React.SetStateAction<TOfficeManager | undefined>
        >;
        updateOffices: React.Dispatch<React.SetStateAction<TOffices | undefined>>;
        office_managers?: TOfficeManager;
        allDepartmentAndServices?: DepartmentCache;
        departments?: DepartmentCache;
        requestedServices?: TRequestedService;
        consentForms?: ConsentFormCache;
        emailSignatures?: EmailSignatureCache;
        location_address?: TLocationAddress;
        updateRequestedServices: React.Dispatch<
            React.SetStateAction<TRequestedService | undefined>
        >;
        updateDepartments: React.Dispatch<
            React.SetStateAction<DepartmentCache | undefined>
        >;
        updateConsentForms: React.Dispatch<
            React.SetStateAction<ConsentFormCache | undefined>
        >;
        updateEmailSignatures: React.Dispatch<
            React.SetStateAction<EmailSignatureCache | undefined>
        >;
        updateAllDeptAndServices: React.Dispatch<
            React.SetStateAction<DepartmentCache | undefined>
        >;
        updateLocationAddress: React.Dispatch<
            React.SetStateAction<TLocationAddress | undefined>
        >;
    }
) {
    return (
        <CookiesProvider>
            <AclContext.Provider value={{ authUser, userAcl, intakeConfig }}>
                <CacheContext.Provider
                    value={{
                        offices,
                        allUserRoles,
                        office_managers,
                        allDepartmentAndServices,
                        updateOffices,
                        updateOfficeManager,
                        departments,
                        consentForms,
                        emailSignatures,
                        location_address,
                        requestedServices,
                        updateRequestedServices,
                        updateDepartments,
                        updateConsentForms,
                        updateEmailSignatures,
                        updateAllDeptAndServices,
                        updateLocationAddress,
                    }}
                >
                    <ChartLockProvider>
                        <Router>
                            <Route path={["/", "/v1", "/v2"]}>
                                <Switch>
                                    {accessControlledRoutes.map((r, idx) => (
                                        <AccessControlledRoute
                                            key={`path-${idx}-${r.path}`}
                                            exact
                                            path={getRoutePaths(r.path)}
                                            component={r.component}
                                        />
                                    ))}
                                    <Route exact path="/">
                                        <Redirect to="/v2/main" />
                                    </Route>
                                    {/* <Route exact path="/v1/">
                                        <Redirect to="/v1/main" />
                                    </Route> */}
                                    <Route exact path="/v2/">
                                        <Redirect to="/v2/main" />
                                    </Route>
                                </Switch>
                            </Route>
                        </Router>
                    </ChartLockProvider>
                </CacheContext.Provider>
            </AclContext.Provider>
        </CookiesProvider>
    );
}

function App() {
    const [checkingAccess, setCheckingAccess] = useState(true);
    const [authUser, setAuthUser] = useState({} as IAuthUser);
    const [userAcl, setUserAcl] = useState<IACLEmail>({} as IACLEmail);
    const [intakeConfig, setAllIntakeConfig] = useState<IntakeConfig>();
    const [availableOffices, setAvailableOffices] = useState<TOffices>();
    const [officeManagers, setOfficeManagers] = useState<TOfficeManager>();
    const [locationAddress, setLocationAddress] = useState<TLocationAddress>();
    const [allAcl, setAllAcl] = useState<IACLEmail[]>();
    const [availableRequestedServices, setAvailableRequestedServices] =
        useState<TRequestedService>();
    const [availableDepartments, setAvailableDepartments] =
        useState<DepartmentCache>();
    const [availableConsentForms, setAvailableConsentForms] =
        useState<ConsentFormCache>();
    const [availableEmailSignatures, setAvailableEmailSignatures] =
        useState<EmailSignatureCache>();
    const [allDepartmentsAndServices, setAllDepartmentsAndServices] =
        useState<DepartmentCache>();

    const cacheAllAcl = (allAcls?: IACLEmail[]) => {
        setAllAcl(allAcls);
        if (allAcls) {
            const officers: string[] = [];
            const colorConfig: { [key: string]: string } = {};
            allAcls?.forEach((acl) => {
                if (acl.email === firebaseAuth.currentUser?.email) {
                    setUserAcl(acl);
                }
                if ((acl.acl?.intake || acl.acl?.billing) && !acl.acl?.blocked) {
                    officers.push(acl.email);
                    if (acl.assignment_color) {
                        colorConfig[acl.email] = acl.assignment_color;
                    }
                }
            });
            setAllIntakeConfig({
                intakeOfficerColors: colorConfig,
                intakeOfficers: officers,
            });
        }
    };

    useEffect(() => {
        let generation = 0;
        let stopMembership: Unsubscribe | undefined;
        const refreshAccess = httpsCallable(fxs, "httpFunctionRefreshIntakeAccess");
        const stopAuth = firebaseAuth.onAuthStateChanged(async (user) => {
            const current = ++generation;
            let workspaceReady = false;
            setCheckingAccess(true);
            stopMembership?.();
            stopMembership = undefined;
            setUserAcl({} as IACLEmail);
            setAllAcl(undefined);
            setAllIntakeConfig(undefined);
            if (!user?.email) {
                setAuthUser({ user });
                setCheckingAccess(false);
                return;
            }
            const denyAccess = () => {
                if (current !== generation) return;
                workspaceReady = false;
                setAuthUser({ user, acl: { blocked: true } });
                setUserAcl({} as IACLEmail);
                setAllAcl(undefined);
                setAllIntakeConfig(undefined);
                setCheckingAccess(false);
            };
            setAuthUser({ user });
            try {
                // Admission runs before any private configuration/directory reads.
                await refreshAccess();
                if (current !== generation) return;
                let loadGeneration = 0;
                stopMembership = onSnapshot(
                    doc(dbv9, "intake_access", user.uid),
                    { includeMetadataChanges: true },
                    async (snapshot) => {
                        if (current !== generation) return;
                        if (snapshot.metadata.fromCache) return;
                        const load = ++loadGeneration;
                        const membership = snapshot.data();
                        const admitted =
                            membership?.active === true &&
                            membership.email === user.email?.toLowerCase() &&
                            (user.emailVerified ||
                                membership.allow_unverified === true);
                        if (!admitted) {
                            denyAccess();
                            return;
                        }
                        if (!workspaceReady) setCheckingAccess(true);
                        try {
                            const [
                                acl,
                                offices,
                                officeManager,
                                requestedServices,
                                departments,
                                consentForms,
                                emailSignatures,
                                locationAddress,
                                allDeptAndServices,
                                clinician,
                                allAcls,
                            ] = await Promise.all([
                                store.getAClByEmail(user.email!),
                                getAllOffices(),
                                getOfficeManagers(),
                                getRequestedServices(),
                                getDepartments(),
                                getConsentForms(),
                                getEmailSignatures(),
                                getLocationAddress(),
                                store.fetchDepartmentAndServices(),
                                store.getClinicianByEmail(user.email!),
                                store.getAllACL(),
                            ]);
                            if (current !== generation || load !== loadGeneration)
                                return;
                            if (acl?.deleted || acl?.acl?.blocked) {
                                denyAccess();
                                return;
                            }
                            setAuthUser({
                                user,
                                acl: acl?.acl,
                                deleted: acl?.deleted,
                                clinician: membership.clinician
                                    ? clinician
                                    : undefined,
                            });
                            setAvailableOffices(offices);
                            setOfficeManagers(officeManager);
                            setAvailableRequestedServices(requestedServices);
                            setAvailableDepartments(departments);
                            setAvailableConsentForms(consentForms);
                            setAvailableEmailSignatures(emailSignatures);
                            setLocationAddress(locationAddress);
                            setAllDepartmentsAndServices(allDeptAndServices);
                            cacheAllAcl(allAcls);
                            workspaceReady = true;
                            setCheckingAccess(false);
                            const googleName = user.providerData.find(
                                (p) => p.providerId === "google.com"
                            )?.displayName;
                            if (googleName && googleName !== user.displayName) {
                                void updateProfile(user, {
                                    displayName: googleName,
                                }).catch(() => {});
                            }
                        } catch {
                            if (load === loadGeneration) denyAccess();
                        }
                    },
                    () => {
                        loadGeneration++;
                        denyAccess();
                    }
                );
            } catch {
                denyAccess();
            }
        });
        return () => {
            generation++;
            stopAuth();
            stopMembership?.();
        };
    }, []);
    if (checkingAccess) {
        return (
            <Loader show type="page" loadingText="Getting your workspace ready" />
        );
    }
    return router(authUser, userAcl, (intakeConfig || {}) as IntakeConfig, {
        offices: availableOffices,
        allUserRoles: allAcl,
        office_managers: officeManagers,
        allDepartmentAndServices: allDepartmentsAndServices,
        updateOffices: setAvailableOffices,
        updateOfficeManager: setOfficeManagers,
        requestedServices: availableRequestedServices,
        departments: availableDepartments,
        consentForms: availableConsentForms,
        emailSignatures: availableEmailSignatures,
        location_address: locationAddress,
        updateRequestedServices: setAvailableRequestedServices,
        updateDepartments: setAvailableDepartments,
        updateConsentForms: setAvailableConsentForms,
        updateEmailSignatures: setAvailableEmailSignatures,
        updateAllDeptAndServices: setAllDepartmentsAndServices,
        updateLocationAddress: setLocationAddress,
    });
}

export default App;
